import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { XCircleIcon, PlusCircleIcon } from '@heroicons/react/outline';

// Define interface for full Solution object
interface Solution {
  id: string;
  title: string;
  tagline: string;
  description: string;
  challenges: string[];
  sectors: string[];
  features: {
    title: string;
    description: string;
  }[];
  techStack: string[];
  animation: {
    nodes: {
      id: string;
      label: string;
      type: string;
      x: number;
      y: number;
    }[];
    edges: {
      from: string;
      to: string;
    }[];
  };
  implementationSteps: {
    id: number;
    title: string;
    description: string;
    iconName: string;
  }[];
  timeline: string;
  pricing: {
    upfront: string;
    monthly?: string;
  };
  tier: 'bronze' | 'silver' | 'gold';
  icon: string;
  media?: {
    type: 'image' | 'video';
    url: string;
    fileName: string;
  };
  productUrl?: string;
}

// New solution with default values
const newSolution: Solution = {
  id: '',
  title: '',
  tagline: '',
  description: '',
  challenges: [],
  sectors: [],
  features: [{ title: '', description: '' }],
  techStack: [],
  animation: {
    nodes: [
      { id: 'node1', label: 'Input', type: 'input', x: 100, y: 100 },
      { id: 'node2', label: 'Process', type: 'process', x: 300, y: 100 },
      { id: 'node3', label: 'Output', type: 'output', x: 500, y: 100 }
    ],
    edges: [
      { from: 'node1', to: 'node2' },
      { from: 'node2', to: 'node3' }
    ]
  },
  implementationSteps: [
    { id: 1, title: '', description: '', iconName: 'CogIcon' }
  ],
  timeline: '4-6 weeks',
  pricing: {
    upfront: '',
    monthly: ''
  },
  tier: 'bronze',
  icon: 'ChartBarIcon',
  productUrl: ''
};

// Define available challenges, sectors, and icons
const availableChallenges = [
  'Limited Market Reach',
  'High Operating Costs',
  'Lack of Skilled Labor',
  'Regulatory Compliance',
  'Cash Flow Management',
  'Insufficient Marketing Skills',
  'Poor Infrastructure',
  'Lack of Business Skills',
  'Competition from Larger Companies',
  'Understanding the Business Environment',
  'Inefficiencies'
];

const availableSectors = [
  'Business',
  'Retail',
  'Marketing',
  'Finance',
  'Logistics',
  'Micro-manufacturing',
  'Professional Services',
  'Manufacturing'
];

const availableIcons = [
  'ChartBarIcon',
  'ChatIcon',
  'UserGroupIcon',
  'ShoppingCartIcon',
  'MailIcon',
  'DocumentTextIcon',
  'CogIcon',
  'GlobeAltIcon',
  'LightningBoltIcon',
  'DatabaseIcon',
  'DesktopComputerIcon',
  'DeviceMobileIcon',
  'ChipIcon',
  'CloudIcon',
  'CloudDownloadIcon',
  'CloudUploadIcon',
  'CodeIcon',
  'CubeIcon',
  'CubeTransparentIcon',
  'CurrencyDollarIcon',
  'ChartPieIcon',
  'ChartSquareBarIcon',
  'PresentationChartLineIcon',
  'ServerIcon',
  'SparklesIcon',
  'LightBulbIcon',
  'LockClosedIcon',
  'PaperAirplaneIcon',
  'PuzzleIcon',
  'TemplateIcon',
  'TerminalIcon',
  'TrendingUpIcon',
  'WifiIcon'
];

const SolutionEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [solution, setSolution] = useState<Solution>(newSolution);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNewSolution, setIsNewSolution] = useState(false);
  const [jsonImport, setJsonImport] = useState('');
  const [showJsonImport, setShowJsonImport] = useState(false);
  const [jsonExport, setJsonExport] = useState('');
  const [showJsonExport, setShowJsonExport] = useState(false);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaUploadSuccess, setMediaUploadSuccess] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch solution data if editing existing solution
  useEffect(() => {
    const fetchSolution = async () => {
      // If no ID, we're creating a new solution
      if (!id) {
        setIsNewSolution(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(`/api/solutions/${id}`);
        
        if (response.data.success) {
          setSolution(response.data.data);
        } else {
          setError('Failed to load solution');
        }
      } catch (err: any) {
        setError(
          err.response?.data?.error || 
          'An error occurred while fetching the solution'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSolution();
  }, [id]);

  // Form input change handlers
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setSolution((prev) => ({ ...prev, [name]: value }));
  };

  const handleNestedInputChange = (
    parentField: string, 
    childField: string, 
    value: string
  ) => {
    setSolution((prev) => {
      const parentValue = prev[parentField as keyof Solution];
      if (typeof parentValue === 'object' && parentValue !== null) {
        return {
          ...prev,
          [parentField]: {
            ...parentValue,
            [childField]: value
          }
        };
      }
      return prev;
    });
  };

  // Array field handlers
  const handleArrayItemChange = <T extends object>(
    field: keyof Solution,
    index: number,
    key: keyof T,
    value: string
  ) => {
    setSolution((prev) => {
      const fieldValue = prev[field];
      if (Array.isArray(fieldValue)) {
        const array = [...fieldValue] as any[];
        array[index] = { ...array[index], [key]: value };
        return { ...prev, [field]: array };
      }
      return prev;
    });
  };

  const addArrayItem = <T extends object | string>(field: keyof Solution, defaultItem: T) => {
    setSolution((prev) => {
      const fieldValue = prev[field];
      if (Array.isArray(fieldValue)) {
        const array = [...fieldValue] as any[];
        return { ...prev, [field]: [...array, defaultItem] };
      }
      return prev;
    });
  };

  const removeArrayItem = (field: keyof Solution, index: number) => {
    setSolution((prev) => {
      const fieldValue = prev[field];
      if (Array.isArray(fieldValue)) {
        const array = [...fieldValue] as any[];
        return { ...prev, [field]: array.filter((_, i) => i !== index) };
      }
      return prev;
    });
  };

  // Multi-select handlers
  const handleMultiSelectChange = (field: 'challenges' | 'sectors', value: string) => {
    setSolution((prev) => {
      const array = [...prev[field]];
      
      if (array.includes(value)) {
        return { ...prev, [field]: array.filter((item) => item !== value) };
      } else {
        return { ...prev, [field]: [...array, value] };
      }
    });
  };

  // Handle JSON import
  const handleJsonImport = () => {
    try {
      const importedSolution = JSON.parse(jsonImport);
      
      // Verify that it has the required fields
      if (!importedSolution.id || !importedSolution.title) {
        setError('Invalid JSON format. Missing required fields.');
        return;
      }
      
      setSolution(importedSolution);
      setShowJsonImport(false);
      setJsonImport('');
      setError(null);
    } catch (err) {
      setError('Invalid JSON format. Please check your input.');
    }
  };

  // Function to generate JSON from current solution
  const handleGenerateJson = () => {
    const formattedSolution = {
      ...solution,
      implementationSteps: solution.implementationSteps.map((step, index) => ({
        ...step,
        id: index + 1
      }))
    };
    
    setJsonExport(JSON.stringify(formattedSolution, null, 2));
    setShowJsonExport(true);
  };

  // Handle media file upload
  const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !solution.id) return;

    const file = files[0];
    const formData = new FormData();
    formData.append('media', file);

    try {
      setMediaUploading(true);
      setMediaUploadSuccess(false);
      setMediaError(null);

      const response = await axios.put(`/api/solutions/${solution.id}/media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSolution(response.data.data);
        setMediaUploadSuccess(true);
        
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (err: any) {
      setMediaError(
        err.response?.data?.error || 
        'An error occurred while uploading the media'
      );
    } finally {
      setMediaUploading(false);
    }
  };

  // Handle media deletion
  const handleDeleteMedia = async () => {
    if (!solution.id) return;

    try {
      setMediaUploading(true);
      setMediaError(null);

      const response = await axios.delete(`/api/solutions/${solution.id}/media`);

      if (response.data.success) {
        // Make sure we maintain the structure of the solution object
        // The server may return an incomplete solution object after deletion
        setSolution(prev => ({
          ...prev,
          media: undefined,
          // Ensure these arrays exist to prevent includes() errors
          challenges: prev.challenges || [],
          sectors: prev.sectors || []
        }));
        setMediaUploadSuccess(true);
      }
    } catch (err: any) {
      setMediaError(
        err.response?.data?.error || 
        'An error occurred while deleting the media'
      );
    } finally {
      setMediaUploading(false);
    }
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Validation
      if (!solution.id) {
        setError('ID is required');
        setSaving(false);
        return;
      }

      if (!solution.title) {
        setError('Title is required');
        setSaving(false);
        return;
      }

      // Formatting - ensure implementationSteps have sequential IDs
      const formattedSolution = {
        ...solution,
        implementationSteps: solution.implementationSteps.map((step, index) => ({
          ...step,
          id: index + 1
        }))
      };

      let response;

      if (isNewSolution) {
        // Create new solution
        response = await axios.post('/api/solutions', formattedSolution);
      } else {
        // Update existing solution
        response = await axios.put(`/api/solutions/${id}`, formattedSolution);
      }

      if (response.data.success) {
        // Navigate to solutions list on success
        navigate('/admin/solutions');
      } else {
        setError('Failed to save solution');
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        'An error occurred while saving the solution'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-foreground dark:text-dark-foreground">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground dark:text-dark-foreground">
          {isNewSolution ? 'Create New Solution' : 'Edit Solution'}
        </h1>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-destructive/10 dark:bg-dark-destructive/10">
          <p className="text-destructive dark:text-dark-destructive">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="p-6 rounded-lg shadow bg-card dark:bg-dark-card">
          <h2 className="mb-4 text-lg font-medium text-foreground dark:text-dark-foreground">
            Basic Information
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label htmlFor="id" className="block mb-1 text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                ID <span className="text-destructive dark:text-dark-destructive">*</span>
              </label>
              <input
                type="text"
                id="id"
                name="id"
                value={solution.id}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                disabled={!isNewSolution}
                required
              />
              {isNewSolution && (
                <p className="mt-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                  URL friendly identifier (e.g., "data-analytics")
                </p>
              )}
            </div>

            <div>
              <label htmlFor="title" className="block mb-1 text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                Title <span className="text-destructive dark:text-dark-destructive">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={solution.title}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                required
              />
            </div>

            <div>
              <label htmlFor="tagline" className="block mb-1 text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                Tagline <span className="text-destructive dark:text-dark-destructive">*</span>
              </label>
              <input
                type="text"
                id="tagline"
                name="tagline"
                value={solution.tagline}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                required
              />
            </div>

            <div>
              <label htmlFor="productUrl" className="block mb-1 text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                SaaS Product URL <span className="text-xs text-foreground/40 font-light">(Leave blank if not a SaaS product)</span>
              </label>
              <input
                type="text"
                id="productUrl"
                name="productUrl"
                value={solution.productUrl || ''}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                placeholder="https://..."
              />
            </div>

            <div>
              <label htmlFor="tier" className="block mb-1 text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                Tier <span className="text-destructive dark:text-dark-destructive">*</span>
              </label>
              <select
                id="tier"
                name="tier"
                value={solution.tier}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                required
              >
                <option value="bronze">Bronze</option>
                <option value="silver">Silver</option>
                <option value="gold">Gold</option>
              </select>
            </div>

            <div>
              <label htmlFor="icon" className="block mb-1 text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                Icon <span className="text-destructive dark:text-dark-destructive">*</span>
              </label>
              <select
                id="icon"
                name="icon"
                value={solution.icon}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                required
              >
                {availableIcons.map((icon) => (
                  <option key={icon} value={icon}>
                    {icon}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label htmlFor="description" className="block mb-1 text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                Description <span className="text-destructive dark:text-dark-destructive">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={solution.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                required
              />
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="p-6 rounded-lg shadow bg-card dark:bg-dark-card">
          <h2 className="mb-4 text-lg font-medium text-foreground dark:text-dark-foreground">
            Categories
          </h2>
          
          <div className="space-y-6">
            <div>
              <label className="block mb-2 text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                Challenges <span className="text-destructive dark:text-dark-destructive">*</span>
              </label>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                {availableChallenges.map((challenge) => (
                  <label key={challenge} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={solution.challenges.includes(challenge)}
                      onChange={() => handleMultiSelectChange('challenges', challenge)}
                      className="rounded border-input dark:border-dark-input text-primary dark:text-white focus:ring-primary dark:focus:ring-white"
                    />
                    <span className="text-sm text-foreground dark:text-dark-foreground">
                      {challenge}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                Sectors <span className="text-destructive dark:text-dark-destructive">*</span>
              </label>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                {availableSectors.map((sector) => (
                  <label key={sector} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={solution.sectors.includes(sector)}
                      onChange={() => handleMultiSelectChange('sectors', sector)}
                      className="rounded border-input dark:border-dark-input text-primary dark:text-white focus:ring-primary dark:focus:ring-white"
                    />
                    <span className="text-sm text-foreground dark:text-dark-foreground">
                      {sector}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="p-6 rounded-lg shadow bg-card dark:bg-dark-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-foreground dark:text-dark-foreground">
              Features
            </h2>
            <button
              type="button"
              onClick={() => addArrayItem('features', { title: '', description: '' })}
              className="inline-flex items-center text-sm text-primary dark:text-white"
            >
              <PlusCircleIcon className="w-5 h-5 mr-1" />
              Add Feature
            </button>
          </div>
          
          {solution.features.map((feature, index) => (
            <div key={index} className="p-4 mb-4 border rounded-md border-border dark:border-dark-border bg-background dark:bg-dark-background">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-medium text-foreground dark:text-dark-foreground">
                  Feature {index + 1}
                </h3>
                {solution.features.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeArrayItem('features', index)}
                    className="text-destructive dark:text-dark-destructive"
                  >
                    <XCircleIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block mb-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                    Title
                  </label>
                  <input
                    type="text"
                    value={feature.title}
                    onChange={(e) => handleArrayItemChange('features', index, 'title' as keyof typeof feature, e.target.value)}
                    className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                    Description
                  </label>
                  <input
                    type="text"
                    value={feature.description}
                    onChange={(e) => handleArrayItemChange('features', index, 'description' as keyof typeof feature, e.target.value)}
                    className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Implementation Steps */}
        <div className="p-6 rounded-lg shadow bg-card dark:bg-dark-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-foreground dark:text-dark-foreground">
              Implementation Steps
            </h2>
            <button
              type="button"
              onClick={() => addArrayItem(
                'implementationSteps', 
                { 
                  id: solution.implementationSteps.length + 1, 
                  title: '', 
                  description: '', 
                  iconName: 'CogIcon' 
                }
              )}
              className="inline-flex items-center text-sm text-primary dark:text-white"
            >
              <PlusCircleIcon className="w-5 h-5 mr-1" />
              Add Step
            </button>
          </div>
          
          {solution.implementationSteps.map((step, index) => (
            <div key={index} className="p-4 mb-4 border rounded-md border-border dark:border-dark-border bg-background dark:bg-dark-background">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-medium text-foreground dark:text-dark-foreground">
                  Step {index + 1}
                </h3>
                {solution.implementationSteps.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeArrayItem('implementationSteps', index)}
                    className="text-destructive dark:text-dark-destructive"
                  >
                    <XCircleIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="block mb-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                    Title
                  </label>
                  <input
                    type="text"
                    value={step.title}
                    onChange={(e) => handleArrayItemChange('implementationSteps', index, 'title' as keyof typeof step, e.target.value)}
                    className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block mb-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                    Description
                  </label>
                  <input
                    type="text"
                    value={step.description}
                    onChange={(e) => handleArrayItemChange('implementationSteps', index, 'description' as keyof typeof step, e.target.value)}
                    className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                    Icon
                  </label>
                  <select
                    value={step.iconName}
                    onChange={(e) => handleArrayItemChange('implementationSteps', index, 'iconName' as keyof typeof step, e.target.value)}
                    className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                  >
                    {availableIcons.map((icon) => (
                      <option key={icon} value={icon}>
                        {icon}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tech Stack */}
        <div className="p-6 rounded-lg shadow bg-card dark:bg-dark-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-foreground dark:text-dark-foreground">
              Tech Stack
            </h2>
            <button
              type="button"
              onClick={() => addArrayItem('techStack', '')}
              className="inline-flex items-center text-sm text-primary dark:text-white"
            >
              <PlusCircleIcon className="w-5 h-5 mr-1" />
              Add Technology
            </button>
          </div>
          
          <div className="space-y-3">
            {solution.techStack.map((tech, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={tech}
                  onChange={(e) => handleArrayItemChange('techStack', index, '0', e.target.value)}
                  className="flex-1 p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                  placeholder="Technology name"
                />
                <button
                  type="button"
                  onClick={() => removeArrayItem('techStack', index)}
                  className="text-destructive dark:text-dark-destructive"
                >
                  <XCircleIcon className="w-5 h-5" />
                </button>
              </div>
            ))}
            {solution.techStack.length === 0 && (
              <p className="text-sm text-muted-foreground dark:text-dark-muted-foreground">
                No technologies added yet.
              </p>
            )}
          </div>
        </div>

        {/* Animation/Flow Diagram */}
        <div className="p-6 rounded-lg shadow bg-card dark:bg-dark-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-foreground dark:text-dark-foreground">
              Solution Flow Diagram
            </h2>
          </div>
          
          <div className="space-y-4">
            <div className="p-3 text-sm rounded bg-muted dark:bg-dark-muted">
              <p className="text-foreground dark:text-dark-foreground">
                The animation diagram defines how the solution workflow is visualized. Add nodes and connections to represent your solution.
              </p>
            </div>
            
            <div>
              <h3 className="mb-2 font-medium text-md text-foreground dark:text-dark-foreground">
                Nodes
              </h3>
              
              {solution.animation.nodes.map((node, index) => (
                <div key={index} className="p-4 mb-4 border rounded-md border-border dark:border-dark-border bg-background dark:bg-dark-background">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="text-sm font-medium text-foreground dark:text-dark-foreground">
                      Node {index + 1}
                    </h4>
                    {solution.animation.nodes.length > 2 && (
                      <button
                        type="button"
                        onClick={() => {
                          setSolution(prev => {
                            const newNodes = [...prev.animation.nodes];
                            newNodes.splice(index, 1);
                            
                            // Also remove any edges connected to this node
                            const nodeId = prev.animation.nodes[index].id;
                            const newEdges = prev.animation.edges.filter(
                              edge => edge.from !== nodeId && edge.to !== nodeId
                            );
                            
                            return {
                              ...prev,
                              animation: {
                                nodes: newNodes,
                                edges: newEdges
                              }
                            };
                          });
                        }}
                        className="text-destructive dark:text-dark-destructive"
                      >
                        <XCircleIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <div>
                      <label className="block mb-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                        ID
                      </label>
                      <input
                        type="text"
                        value={node.id}
                        onChange={(e) => {
                          setSolution(prev => {
                            const newNodes = [...prev.animation.nodes];
                            const oldId = newNodes[index].id;
                            newNodes[index] = { ...newNodes[index], id: e.target.value };
                            
                            // Also update any edges that use this node
                            const newEdges = prev.animation.edges.map(edge => {
                              if (edge.from === oldId) {
                                return { ...edge, from: e.target.value };
                              }
                              if (edge.to === oldId) {
                                return { ...edge, to: e.target.value };
                              }
                              return edge;
                            });
                            
                            return {
                              ...prev,
                              animation: {
                                nodes: newNodes,
                                edges: newEdges
                              }
                            };
                          });
                        }}
                        className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                        Label
                      </label>
                      <input
                        type="text"
                        value={node.label}
                        onChange={(e) => {
                          setSolution(prev => {
                            const newNodes = [...prev.animation.nodes];
                            newNodes[index] = { ...newNodes[index], label: e.target.value };
                            return {
                              ...prev,
                              animation: {
                                ...prev.animation,
                                nodes: newNodes
                              }
                            };
                          });
                        }}
                        className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                        Type
                      </label>
                      <select
                        value={node.type}
                        onChange={(e) => {
                          setSolution(prev => {
                            const newNodes = [...prev.animation.nodes];
                            newNodes[index] = { ...newNodes[index], type: e.target.value };
                            return {
                              ...prev,
                              animation: {
                                ...prev.animation,
                                nodes: newNodes
                              }
                            };
                          });
                        }}
                        className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                      >
                        <option value="input">Input</option>
                        <option value="process">Process</option>
                        <option value="output">Output</option>
                        <option value="data">Data</option>
                        <option value="decision">Decision</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block mb-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                          X Position
                        </label>
                        <input
                          type="number"
                          value={node.x}
                          onChange={(e) => {
                            setSolution(prev => {
                              const newNodes = [...prev.animation.nodes];
                              newNodes[index] = { ...newNodes[index], x: parseInt(e.target.value) || 0 };
                              return {
                                ...prev,
                                animation: {
                                  ...prev.animation,
                                  nodes: newNodes
                                }
                              };
                            });
                          }}
                          className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                        />
                      </div>
                      <div>
                        <label className="block mb-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                          Y Position
                        </label>
                        <input
                          type="number"
                          value={node.y}
                          onChange={(e) => {
                            setSolution(prev => {
                              const newNodes = [...prev.animation.nodes];
                              newNodes[index] = { ...newNodes[index], y: parseInt(e.target.value) || 0 };
                              return {
                                ...prev,
                                animation: {
                                  ...prev.animation,
                                  nodes: newNodes
                                }
                              };
                            });
                          }}
                          className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              <button
                type="button"
                onClick={() => {
                  setSolution(prev => {
                    const nodeId = `node${prev.animation.nodes.length + 1}`;
                    const newNode = {
                      id: nodeId,
                      label: 'New Node',
                      type: 'process',
                      x: 300,
                      y: 100 + prev.animation.nodes.length * 100
                    };
                    
                    return {
                      ...prev,
                      animation: {
                        ...prev.animation,
                        nodes: [...prev.animation.nodes, newNode]
                      }
                    };
                  });
                }}
                className="mt-2 inline-flex items-center text-sm font-medium px-3 py-1.5 rounded bg-primary text-white hover:bg-primary/90 dark:bg-primary-500 dark:text-white dark:hover:bg-primary-600"
              >
                <PlusCircleIcon className="w-5 h-5 mr-1" />
                Add Node
              </button>
            </div>
            
            <div className="mt-6">
              <h3 className="mb-2 font-medium text-md text-foreground dark:text-dark-foreground">
                Connections
              </h3>
              
              {solution.animation.edges.map((edge, index) => (
                <div key={index} className="p-4 mb-4 border rounded-md border-border dark:border-dark-border bg-background dark:bg-dark-background">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="text-sm font-medium text-foreground dark:text-dark-foreground">
                      Connection {index + 1}
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        setSolution(prev => {
                          const newEdges = [...prev.animation.edges];
                          newEdges.splice(index, 1);
                          return {
                            ...prev,
                            animation: {
                              ...prev.animation,
                              edges: newEdges
                            }
                          };
                        });
                      }}
                      className="text-destructive dark:text-dark-destructive"
                    >
                      <XCircleIcon className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block mb-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                        From Node
                      </label>
                      <select
                        value={edge.from}
                        onChange={(e) => {
                          setSolution(prev => {
                            const newEdges = [...prev.animation.edges];
                            newEdges[index] = { ...newEdges[index], from: e.target.value };
                            return {
                              ...prev,
                              animation: {
                                ...prev.animation,
                                edges: newEdges
                              }
                            };
                          });
                        }}
                        className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                      >
                        {solution.animation.nodes.map((node) => (
                          <option key={node.id} value={node.id}>
                            {node.label} ({node.id})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block mb-1 text-xs text-muted-foreground dark:text-dark-muted-foreground">
                        To Node
                      </label>
                      <select
                        value={edge.to}
                        onChange={(e) => {
                          setSolution(prev => {
                            const newEdges = [...prev.animation.edges];
                            newEdges[index] = { ...newEdges[index], to: e.target.value };
                            return {
                              ...prev,
                              animation: {
                                ...prev.animation,
                                edges: newEdges
                              }
                            };
                          });
                        }}
                        className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                      >
                        {solution.animation.nodes.map((node) => (
                          <option key={node.id} value={node.id}>
                            {node.label} ({node.id})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
              
              <button
                type="button"
                onClick={() => {
                  if (solution.animation.nodes.length < 2) {
                    setError('You need at least two nodes to create a connection');
                    return;
                  }
                  
                  setSolution(prev => {
                    const newEdge = {
                      from: prev.animation.nodes[0].id,
                      to: prev.animation.nodes[1].id
                    };
                    
                    return {
                      ...prev,
                      animation: {
                        ...prev.animation,
                        edges: [...prev.animation.edges, newEdge]
                      }
                    };
                  });
                }}
                className="mt-2 inline-flex items-center text-sm font-medium px-3 py-1.5 rounded bg-primary text-white hover:bg-primary/90 dark:bg-primary-500 dark:text-white dark:hover:bg-primary-600"
              >
                <PlusCircleIcon className="w-5 h-5 mr-1" />
                Add Connection
              </button>
            </div>
          </div>
        </div>

        {/* Import JSON */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowJsonImport(!showJsonImport)}
            className="text-sm font-medium text-primary dark:text-slate-300 hover:text-primary/90 dark:hover:text-white"
          >
            {showJsonImport ? 'Hide JSON Import' : 'Import from JSON'}
          </button>
          
          {showJsonImport && (
            <div className="p-6 mt-4 rounded-lg shadow bg-card dark:bg-dark-card">
              <h2 className="mb-4 text-lg font-medium text-foreground dark:text-slate-100">
                Import Solution from JSON
              </h2>
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-foreground dark:text-slate-300">
                  Paste Solution JSON
                </label>
                <textarea
                  value={jsonImport}
                  onChange={(e) => setJsonImport(e.target.value)}
                  rows={10}
                  className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                  placeholder="Paste solution JSON here..."
                />
              </div>
              
              <div className="mb-4">
                <details className="text-sm">
                  <summary className="font-medium cursor-pointer text-primary dark:text-slate-300">
                    Show JSON Schema Documentation
                  </summary>
                  <div className="p-3 mt-2 overflow-auto rounded bg-muted dark:bg-dark-muted text-foreground dark:text-slate-300">
                    <pre className="text-xs whitespace-pre-wrap">
{`{
  "id": "string", // Required: Unique identifier (e.g., "data-analytics")
  "title": "string", // Required: Solution title
  "tagline": "string", // Required: Short description
  "description": "string", // Required: Full description
  "challenges": ["string"], // Array of challenge names
  "sectors": ["string"], // Array of sector names
  "features": [
    {
      "title": "string", // Feature name
      "description": "string" // Feature description
    }
  ],
  "techStack": ["string"], // Array of technology names
  "animation": {
    "nodes": [
      {
        "id": "string", // Node identifier
        "label": "string", // Node label
        "type": "string", // Node type (input, process, output, data, decision)
        "x": number, // X position
        "y": number // Y position
      }
    ],
    "edges": [
      {
        "from": "string", // Source node ID
        "to": "string" // Target node ID
      }
    ]
  },
  "implementationSteps": [
    {
      "id": number, // Step number
      "title": "string", // Step title
      "description": "string", // Step description
      "iconName": "string" // Icon name from available icons
    }
  ],
  "timeline": "string", // Implementation timeframe (e.g., "4-6 weeks")
  "pricing": {
    "upfront": "string", // Required: Upfront cost (e.g., "R15,000")
    "monthly": "string" // Optional: Monthly cost (e.g., "R1,500 / month")
  },
  "tier": "bronze" | "silver" | "gold", // Required: Solution tier
  "icon": "string" // Required: Icon name from available icons
}`}
                    </pre>
                  </div>
                </details>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleJsonImport}
                  className="px-4 py-2 text-white rounded-md bg-primary hover:bg-primary/90 dark:bg-dark-primary dark:hover:bg-dark-primary/90"
                >
                  Import
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Generate JSON */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => {
              if (showJsonExport) {
                setShowJsonExport(false);
              } else {
                handleGenerateJson();
              }
            }}
            className="text-sm font-medium text-primary dark:text-slate-300 hover:text-primary/90 dark:hover:text-white"
          >
            {showJsonExport ? 'Hide JSON' : 'Generate JSON'}
          </button>
          
          {showJsonExport && (
            <div className="p-6 mt-4 rounded-lg shadow bg-card dark:bg-dark-card">
              <h2 className="mb-4 text-lg font-medium text-foreground dark:text-slate-100">
                Solution JSON
              </h2>
              <div className="mb-4">
                <div className="relative">
                  <pre className="p-3 overflow-auto text-xs rounded bg-muted dark:bg-dark-muted text-foreground dark:text-slate-300 max-h-96">
                    {jsonExport}
                  </pre>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(jsonExport);
                      // You could add a toast notification here
                    }}
                    className="absolute px-2 py-1 text-xs text-white rounded top-2 right-2 bg-primary hover:bg-primary/90 dark:bg-primary-500 dark:hover:bg-primary-600"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Timeline and Pricing */}
        <div className="p-6 rounded-lg shadow bg-card dark:bg-dark-card">
          <h2 className="mb-4 text-lg font-medium text-foreground dark:text-dark-foreground">
            Timeline and Pricing
          </h2>
          
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div>
              <label htmlFor="timeline" className="block mb-1 text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                Timeline <span className="text-destructive dark:text-dark-destructive">*</span>
              </label>
              <input
                type="text"
                id="timeline"
                name="timeline"
                value={solution.timeline}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                placeholder="e.g., 4-6 weeks"
                required
              />
            </div>

            <div>
              <label htmlFor="pricing-upfront" className="block mb-1 text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                Upfront Price <span className="text-destructive dark:text-dark-destructive">*</span>
              </label>
              <input
                type="text"
                id="pricing-upfront"
                value={solution.pricing.upfront}
                onChange={(e) => handleNestedInputChange('pricing', 'upfront', e.target.value)}
                className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                placeholder="e.g., R15,000"
                required
              />
            </div>

            <div>
              <label htmlFor="pricing-monthly" className="block mb-1 text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">
                Monthly Price (optional)
              </label>
              <input
                type="text"
                id="pricing-monthly"
                value={solution.pricing.monthly || ''}
                onChange={(e) => handleNestedInputChange('pricing', 'monthly', e.target.value)}
                className="w-full p-2 border rounded-md border-input dark:border-dark-input bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground"
                placeholder="e.g., R1,500 / month"
              />
            </div>
          </div>
        </div>

        {/* Media Upload Section */}
        <div className="p-6 rounded-lg shadow bg-card dark:bg-dark-card">
          <h2 className="text-lg font-medium text-foreground dark:text-dark-foreground">Solution Media</h2>
          <p className="mt-1 text-sm text-muted-foreground dark:text-dark-muted-foreground">
            Upload an image or video to showcase in the solution features.
          </p>

          {mediaError && (
            <div className="mt-4 p-3 border rounded bg-destructive/10 border-destructive/20 text-destructive dark:bg-dark-destructive/10 dark:border-dark-destructive/20 dark:text-dark-destructive">
              {mediaError}
            </div>
          )}

          {mediaUploadSuccess && (
            <div className="mt-4 p-3 border rounded bg-success/10 border-success/20 text-success dark:bg-dark-success/10 dark:border-dark-success/20 dark:text-dark-success">
              Media updated successfully.
            </div>
          )}

          <div className="mt-6 space-y-6">
            {solution.media ? (
              <div className="space-y-4">
                <div>
                  <h3 className="text-base font-medium text-foreground dark:text-dark-foreground">Current Media ({solution.media.type})</h3>
                  <div className="mt-2 max-w-lg">
                    {solution.media.type === 'image' ? (
                      <img 
                        src={solution.media.url} 
                        alt={solution.title} 
                        className="w-full h-auto rounded border border-border dark:border-dark-border" 
                      />
                    ) : (
                      <video 
                        src={solution.media.url}
                        controls
                        className="w-full rounded border border-border dark:border-dark-border"
                      />
                    )}
                    <p className="mt-1 text-sm text-muted-foreground dark:text-dark-muted-foreground">
                      {solution.media.fileName}
                    </p>
                  </div>
                </div>
                
                <button
                  type="button"
                  onClick={handleDeleteMedia}
                  disabled={mediaUploading}
                  className="inline-flex items-center px-4 py-2 rounded-md shadow-sm text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive disabled:opacity-50 dark:bg-dark-destructive dark:hover:bg-dark-destructive/90 dark:focus:ring-dark-destructive"
                >
                  <XCircleIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                  {mediaUploading ? 'Deleting...' : 'Delete Media'}
                </button>
              </div>
            ) : (
              <p className="text-muted-foreground dark:text-dark-muted-foreground">No media uploaded yet.</p>
            )}

            <div className="mt-4">
              <label htmlFor="media-upload" className="block text-sm font-medium text-foreground dark:text-dark-foreground">
                {solution.media ? 'Replace Media' : 'Upload Media'}
              </label>
              <div className="mt-1 flex items-center">
                <input
                  id="media-upload"
                  name="media"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm"
                  onChange={handleMediaUpload}
                  ref={fileInputRef}
                  disabled={mediaUploading || saving}
                  className="block w-full text-sm text-muted-foreground dark:text-dark-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 dark:file:bg-dark-primary/10 dark:file:text-dark-primary dark:hover:file:bg-dark-primary/20"
                />
                {mediaUploading && (
                  <div className="ml-3">
                    <svg className="animate-spin h-5 w-5 text-primary dark:text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
              </div>
              <p className="mt-2 text-sm text-muted-foreground dark:text-dark-muted-foreground">
                Upload an image (JPEG, PNG, GIF, WebP) or video (MP4, WebM) file (max 10MB).
              </p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/admin/solutions')}
            className="px-4 py-2 rounded-md text-foreground dark:text-dark-foreground bg-muted dark:bg-dark-muted hover:bg-muted/80 dark:hover:bg-dark-muted/80"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-white rounded-md bg-primary hover:bg-primary/90 dark:bg-dark-primary dark:hover:bg-dark-primary/90"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Solution'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SolutionEditPage; 